import type {FeedDetailItem, MappingAudit} from '../types/FeedItem';

type AnyFeed = Record<string, any>;

function labelFromScore(n?: number) {
  if (typeof n !== 'number') return undefined;
  if (n >= 70) return '높음';
  if (n >= 40) return '보통';
  if (n >= 0) return '낮음';
  return undefined;
}

function parseImpactScoreText(raw?: string): { score?: number; label?: string } {
  const text = String(raw ?? '').trim();
  if (!text) return {};

  const mEq = /Score\s*=\s*(\d{1,3})/i.exec(text);
  if (mEq) {
    const n = Math.max(0, Math.min(100, Number(mEq[1])));
    return {score: n, label: labelFromScore(n)};
  }

  const mLead = /^\s*(\d{1,3})(?!\d)/.exec(text);
  if (mLead) {
    const n = Math.max(0, Math.min(100, Number(mLead[1])));
    return {score: n, label: labelFromScore(n)};
  }

  if (/높음/.test(text)) return {label: '높음'};
  if (/보통/.test(text)) return {label: '보통'};
  if (/낮음/.test(text)) return {label: '낮음'};
  if (/무관/.test(text)) return {label: '무관'};

  return {};
}

function stripImpactDebug(raw?: string): string {
  if (!raw) return '';
  let s = String(raw);

  s = s.replace(/\bScore\s*=\s*\d{1,3}.*$/i, '');
  s = s.replace(/—\s*Direct[\s\S]*$/i, '');
  s = s.replace(/\s*\|\s*IndirectCap.*$/i, '');
  s = s.replace(/(∑conf|W=|PolicyTrigger|IndirectCap|승격|보정)[\s\S]*$/g, '');
  s = s.replace(/[*★()%]+/g, '').trim();

  return s;
}

function parseJsonLoose(value: any): any {
  if (!value) return null;
  if (typeof value === 'object') return value;
  if (typeof value !== 'string') return null;
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
}

function toNumber(value: any): number | null {
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function normalizeScoreToPct(value: any): number | null {
  const n = toNumber(value);
  if (n === null) return null;
  if (n <= 1) return Math.round(n * 100);
  return Math.round(Math.max(0, Math.min(100, n)));
}

function cleanText(value: any): string {
  return String(value ?? '')
  .replace(/&amp;/g, '&')
  .replace(/\s+/g, ' ')
  .trim();
}

function cleanStockName(value: any): string {
  return cleanText(value)
  .replace(/\(\s*A?\d{6}\s*\)/g, '')
  .replace(/\bA?\d{6}\b/g, '')
  .replace(/^\d+\.\s*/, '')
  .replace(/\[직접\]/g, '')
  .replace(/\s*[:：].*$/g, '')
  .trim();
}

function buildImpactHeaderFromProvenance(prov?: string | Record<string, any>): string {
  if (!prov) return '';
  let obj: any = prov;
  try {
    if (typeof prov === 'string') obj = JSON.parse(prov);
  } catch {
    return '';
  }

  const raw = String(obj?.['영향도'] ?? '').trim();
  if (!raw) return '';

  const {score, label} = parseImpactScoreText(raw);
  if (typeof score === 'number') {
    return `정책 노출 강도: ${score}점${label ? ` (${label})` : ''}`;
  }
  if (label) return `정책 노출 강도: ${label}`;
  return '';
}

function parseDirectKorAudit(value: any): Partial<MappingAudit> | null {
  const text = String(value ?? '').trim();
  if (!text) return null;

  const m = /(?:\[직접\]|direct)?\s*([^(:[]+?)\s*\((?:A)?(\d{6})\)\s*:?\s*(.*?)(?:\s*\/\s*score\s*=\s*([0-9.]+))?\s*$/i.exec(text);
  if (!m) return null;

  const score = normalizeScoreToPct(m[4]);

  return {
    ticker: m[2],
    corp_name: cleanStockName(m[1]),
    mapping_score: score,
    mapping_strength: score !== null && score <= 20 ? 'weak' : 'medium',
    display_decision: score !== null && score <= 20 ? 'Watch' : 'Review',
    final_audit_decision: score !== null && score <= 20 ? 'direct_mention_but_profile_weak' : 'mapped_but_manual_review_required',
    audit_reason: score !== null && score <= 20 ? '원문 직접 언급은 있으나 사업보고서 근거가 약해 검증 필요' : '원문 직접 언급 후보이나 근거 상세 확인 필요',
  };
}

function normalizeMappingAudit(src: AnyFeed): MappingAudit | null {
  const raw = src.mapping_audit ?? src.mappingAudit;
  if (raw && typeof raw === 'object') {
    return raw as MappingAudit;
  }

  const metadata = parseJsonLoose(src.metadata);
  const metaAudit = metadata?.mapping_audit ?? metadata?.mappingAudit;
  if (metaAudit && typeof metaAudit === 'object') return metaAudit as MappingAudit;

  if (src.final_audit_decision || src.audit_reason || src.audit_ticker || src.audit_corp_name) {
    return {
      ticker: src.audit_ticker ?? src.ticker ?? null,
      corp_name: src.audit_corp_name ?? src.corp_name ?? null,
      issue_term: src.audit_issue_term ?? src.profile_issue_term ?? null,
      profile_term_decision: src.audit_profile_term_decision ?? src.profile_term_decision ?? null,
      profile_term_confidence: src.audit_profile_term_confidence ?? src.profile_term_confidence ?? null,
      profile_term_reason_code: src.audit_profile_term_reason_code ?? src.profile_term_reason_code ?? null,
      final_audit_decision: src.final_audit_decision ?? null,
      audit_reason: src.audit_reason ?? null,
      display_decision: src.display_decision ?? null,
      mapping_strength: src.mapping_strength ?? null,
      mapping_score: src.mapping_score ?? null,
    };
  }

  const directAudit = parseDirectKorAudit(src.direct_kor ?? src.directKor);
  if (directAudit) return directAudit as MappingAudit;

  const impactReason = cleanText(src.impact_reason ?? src.impactReason ?? '');
  if (/NO_ACTIONABLE_MAPPING|no_actionable_stock_mapping/i.test(impactReason)) {
    return {
      final_audit_decision: 'no_actionable_mapping',
      audit_reason: '제외',
      display_decision: 'Exclude',
      mapping_strength: 'none',
      mapping_score: 0,
    };
  }

  return null;
}

const FIELD_MAP: Record<string, string> = {
  publishedAt: 'published_at',
  impactScore: 'impact_score',
  impactScoreReason: 'impact_score_reason',
  policySummary: 'policy_summary',
  impactAnalysis: 'impact_analysis',
  investmentNote: 'investment_note',
  gptAnalysis: 'gpt_analysis',
  gptRaw: 'gpt_raw',
  matchedThemes: 'matched_themes',
  issueTags: 'issue_tags',
  dataProvenance: 'data_provenance',
  provenanceReason: 'provenance_reason',
  sourceClassification: 'source_classification',
  sourceReason: 'source_reason',
  betaNotice: 'beta_notice',
  titleKo: 'title_ko',
  summaryKo: 'summary_ko',
  usTicker: 'us_ticker',
  directKor: 'direct_kor',
  indirectKor: 'indirect_kor',
  relatedKor: 'related_kor',
  mappingAudit: 'mapping_audit',
  finalAuditDecision: 'final_audit_decision',
  auditReason: 'audit_reason',
  displayDecision: 'display_decision',
  mappingStrength: 'mapping_strength',
};

export function normalizeForDetail<T extends Partial<FeedDetailItem>>(x: T): FeedDetailItem {
  const src: AnyFeed = {...x};

  Object.entries(FIELD_MAP).forEach(([from, to]) => {
    if (src[from] !== undefined && src[to] === undefined) {
      src[to] = src[from];
    }
  });

  const id = String(src.feed_id ?? src.id ?? src.feedId ?? '');
  src.feed_id = id;
  src.id = id;

  const title = String(src.title_ko ?? src.title ?? '');
  const summaryRaw = String(src.summary_ko ?? src.summary ?? '');
  const summaryClean = stripImpactDebug(summaryRaw);

  const impactHeader = buildImpactHeaderFromProvenance(
      src.provenance_reason ?? src.provenanceReason,
  );

  const mappingAudit = normalizeMappingAudit(src);

  return {
    id,
    feed_id: id,
    title,
    summary: summaryClean,

    title_ko: src.title_ko ?? '',
    summary_ko: src.summary_ko ?? (summaryClean || ''),
    published_at: src.published_at ?? '',
    impact_score: Number.isFinite(Number(src.impact_score)) ? Number(src.impact_score) : 0,

    content: src.content ?? '',
    content_ko: src.content_ko ?? '',
    translated_content: src.translated_content ?? '',
    summaryDetail: src.summaryDetail ?? '',
    explainer: src.explainer ?? '',

    impact_analysis: src.impact_analysis ?? '',
    policy_summary: src.policy_summary ?? '',
    investment_note: src.investment_note ?? '',
    impact_reason: src.impact_reason ?? '',
    impact_score_reason: src.impact_score_reason ?? '',

    gpt_analysis: src.gpt_analysis ?? {},
    gpt_raw: src.gpt_raw ?? '',
    matched_themes: src.matched_themes ?? [],
    issue_tags: src.issue_tags ?? [],
    theme_return_data: src.theme_return_data ?? {},

    data_provenance: src.data_provenance ?? '',
    provenance_reason: src.provenance_reason ?? {},
    impact_header: impactHeader,

    similar_news: src.similar_news ?? [],
    similarity_stats: src.similarity_stats ?? '',

    us_ticker: src.us_ticker ?? '',
    direct_kor: src.direct_kor ?? '',
    indirect_kor: src.indirect_kor ?? '',
    related_kor: src.related_kor ?? '',

    mapping_audit: mappingAudit,
    final_audit_decision: src.final_audit_decision ?? mappingAudit?.final_audit_decision ?? null,
    audit_reason: src.audit_reason ?? mappingAudit?.audit_reason ?? null,
    display_decision: src.display_decision ?? mappingAudit?.display_decision ?? null,
    mapping_strength: src.mapping_strength ?? mappingAudit?.mapping_strength ?? null,

    source_url: src.source_url ?? '',
    source_name: src.source_name ?? '',
    country: src.country ?? '',
    keywords: src.keywords ?? [],

    ...(src as AnyFeed),
  } as FeedDetailItem;
}
